/* eslint-disable function-paren-newline */
import { CartItem, CartItemDetails } from '@customTypes/cart-items';
import HotelDetails from '@customTypes/hotel-details';
import {
  isProductUnAvailable,
  numberOfAvailableUnits,
  showAvailabilityWarning,
} from '@helpers/product';
import React from 'react';
import ProductData from '@customTypes/product';
import { isArray, isEmpty } from 'lodash';
import useSoldOut from '@hooks/useSoldOut';
import useHotSpot from '@hooks/useHotSpot';
import Badge from '@components/common/Badge/Badge';

export const productTagsClass = {
  IN_CART: 'bg-rp-blue-badge text-rp-blue-badge-text',
  UNITS_LEFT: 'bg-solar-surface-warning text-solar-alert-amber',
  UNAVAILABLE: 'bg-rp-gray-divider text-solar-secondary',
  PRICING_MARKDOWN: 'bg-rp-green-badge text-rp-green-badge-text',
  HOT_SPOT: 'bg-rp-hot-spot text-white',
};

type Props = {
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  hotelDetails: HotelDetails;
  product: ProductData;
  personType?: 'adult' | 'child';
  individualBilling?: boolean;
  hideDiscountPrice?: boolean;
};

function ProductTagFactoryWithSTP({
  date,
  cartData,
  hotelDetails,
  product,
  individualBilling = false,
  hideDiscountPrice = false,
  personType = 'adult',
}: Props) {
  const { items: cartItems } = cartData || {};
  const { getSoldOutTextForProduct } = useSoldOut();
  const { getDiscountText } = useHotSpot();

  const showAvailableUnits =
    date && showAvailabilityWarning(product, cartData) && !product.is_product_time_group;

  let matchingProduct;
  let tagContent = '';
  let tagClass = '';

  // Find the matching product in the cart
  if (product.is_product_time_group) {
    // Extract the product IDs from the children array
    const childProductIds = product.children?.map((childProduct) => childProduct.product_id);

    // Find the products that match any of the child IDs
    matchingProduct = cartItems?.filter((cartItem) =>
      childProductIds?.includes(cartItem.product_id),
    );
  } else if (product.individual_billing) {
    matchingProduct = cartItems?.find((item) => item.product_id === product.product_id);
  } else if (!product.individual_billing) {
    matchingProduct = cartItems?.filter((item) => item.product_id === product.product_id);
  }

  // IN CART tag
  if (cartItems && !isEmpty(matchingProduct) && !individualBilling) {
    // Time Slot product
    if (product.is_product_time_group && isArray(matchingProduct) && matchingProduct.length) {
      let guestCount = 0;
      if (product.individual_billing) {
        guestCount = matchingProduct.reduce(
          (sum: number, item: CartItem) => sum + item.no_of_adults + item.no_of_childs,
          0,
        );
      } else {
        guestCount = matchingProduct.length;
      }

      tagContent = `${guestCount} IN CART`;
      tagClass = productTagsClass.IN_CART;

      // Individual billing product
    } else if (product.individual_billing && !isArray(matchingProduct)) {
      const adultAndChildrenTotal = matchingProduct.no_of_adults + matchingProduct.no_of_childs;

      if (adultAndChildrenTotal > 0) {
        tagContent = `${adultAndChildrenTotal} IN CART`;
        tagClass = productTagsClass.IN_CART;
      }
      // Group billing product
    } else if (!product.individual_billing && isArray(matchingProduct)) {
      if (matchingProduct.length > 0) {
        tagContent = `${matchingProduct.length} IN CART`;
        tagClass = productTagsClass.IN_CART;
      }
    }
    // Available Units tag
  } else if (
    !hideDiscountPrice &&
    !isProductUnAvailable(product, cartData, hotelDetails, date) &&
    product.is_hot_spot &&
    hotelDetails.hot_spot
  ) {
    tagContent = `SAVE ${getDiscountText(hotelDetails.hot_spot)}`;
    tagClass = productTagsClass.HOT_SPOT;
    // STP
  } else if (
    !hideDiscountPrice &&
    !isProductUnAvailable(product, cartData, hotelDetails, date) &&
    ((personType === 'adult' && product.is_adult_strikethrough_pricing) ||
      (personType === 'child' && product.is_child_strikethrough_pricing))
  ) {
    tagContent = 'WEEKDAY RATE';
    tagClass = productTagsClass.PRICING_MARKDOWN;
  } else if (
    showAvailableUnits &&
    date &&
    !isProductUnAvailable(product, cartData, hotelDetails, date)
  ) {
    tagContent = `ONLY ${numberOfAvailableUnits(product, cartData)} LEFT`;
    tagClass = productTagsClass.UNITS_LEFT;
  } else if (date && isProductUnAvailable(product, cartData, hotelDetails, date)) {
    tagContent = getSoldOutTextForProduct(product);
    tagClass = productTagsClass.UNAVAILABLE;
  } else {
    return null;
  }

  return <Badge tagContent={tagContent} tagClass={tagClass} />;
}

export default ProductTagFactoryWithSTP;
